import lodashGet from "lodash/get";
import supercluster from "supercluster";
import { featureCollection } from "@turf/turf";
import { httpGet } from "@/services/api.service";
import { addSource } from "@/common/maps/sources";
import { addLayer } from "@/common/maps/layers";
import {
  getGeohashFeature,
  getBboxFromFeatures,
} from "@/common/maps/utilities";
import {
  getDefaultGeohashMarkerOptions,
  getDefaultHeatMapOptions,
} from "@/common/maps/styles";
import {
  layerClickEventHandlerOn,
  layerClickEventHandlerOff,
} from "@/common/maps/eventHandler";

export { _mapGeohashedAggregation as mapGeohashedAggregation };
export { _removeGeohashAggregation as removeGeohashAggregation };
export { _getFeaturesInGeohashCluster as getFeaturesInGeohashCluster };

let clusters = {};

const CLUSTER_RADIUS = 50;
const CLUSTER_OPTIONS = {
  radius: CLUSTER_RADIUS,
  initial: function () {
    return { sum: 0 };
  },
  map: function (props) {
    if (!isNaN(props.doc_count)) {
      return { sum: props.doc_count };
    }
    props.doc_count = 1;
    return {
      sum: 1,
    };
  },
  reduce: function (accumulated, props) {
    accumulated.sum += props.sum;
    accumulated.sum_formatted = accumulated.sum;
  },
};

const _mapGeohashedAggregation = async (options) => {
  /** OPTIONS
   * id:  source or datalens ID
   * data: endpoint for markers
   * geohashOptions:  options for Geohash agg
   * map: map
   * */

  if (options.map) {
    options.map.off("zoom", _updateClusters);
    options.map.on("zoom", _updateClusters);
  }

  if (typeof options.data === "string") {
    try {
      const RESULT = await httpGet({ url: options.data });
      const { data } = JSON.parse(RESULT);

      if (data) {
        options.data = data;
        _addGeohashData(options);
      }
    } catch (error) {
      return error;
    }
  } else {
    _addGeohashData(options);
  }
};

const _addGeohashData = (options) => {
  if (options && options.heatmap) {
    _addGeohashHeatmapData(options);
  } else {
    _addGeohashMarkerData(options);
  }
};

const _addGeohashHeatmapData = (options) => {
  _addHeatmapSource(options.id, options.map, options.data);

  let params = {
    id: options.id,
    metadata: options.metadata,
  };

  let markerOptions = getDefaultHeatMapOptions(params);
  if (options.geohashOptions) {
    markerOptions = options.geohashOptions;
  }

  addLayer(options.map, markerOptions.heatmapLayerOptions, options.beforeLayer);
};

const _addGeohashMarkerData = (options) => {
  if (!clusters.hasOwnProperty(options.id)) {
    clusters[options.id] = new supercluster(CLUSTER_OPTIONS);
  }
  clusters[options.id].load(options.data.features);

  _updateCluster(options.id, options.map);

  let params = {
    id: options.id,
    metadata: options.metadata,
  };
  if (options.iconName) {
    params.iconName = options.iconName;
  }
  let markerOptions = options.heatmap
    ? getDefaultHeatMapOptions(params)
    : getDefaultGeohashMarkerOptions(params);
  if (options.geohashOptions) {
    markerOptions = options.geohashOptions;
  }

  addLayer(options.map, markerOptions.markerLayerOptions);
  _addGeohashClusterEvents(
    options.map,
    markerOptions.markerLayerOptions.id,
    options.mapClickEvent
  );

  addLayer(options.map, markerOptions.markerClusterLayerOptions);
  _addGeohashClusterEvents(
    options.map,
    markerOptions.markerClusterLayerOptions.id,
    options.clusterMapClickEvent
  );
};

const _removeGeohashAggregation = (map, id) => {
  if (id && clusters && clusters[id]) {
    layerClickEventHandlerOff(map, id + "-geohash");
    layerClickEventHandlerOff(map, id + "-geohash-cluster");
    delete clusters[id];
  }
};

const _addGeohashClusterEvents = (map, id, mapClickEvent) => {
  const MAP_CLICK_EVENT = (e, features) => {
    e.originalEvent.stopPropagation();
    if (features && Array.isArray(features)) {
      const ID = id.replace("-geohash", "").replace("-cluster", "");

      let featuresArr = [];
      let cluster_bbox = null;
      let feature;
      if (lodashGet(features[0], "properties.cluster_id")) {
        const CLUSTER_FEATURES = _getFeaturesInGeohashCluster(
          ID,
          features[0].properties.cluster_id
        );
        if (CLUSTER_FEATURES && Array.isArray(CLUSTER_FEATURES)) {
          const LEN = CLUSTER_FEATURES.length;
          for (let i = 0; i < LEN; i++) {
            if (lodashGet(CLUSTER_FEATURES[i], "properties.key")) {
              feature = getGeohashFeature(
                lodashGet(CLUSTER_FEATURES[i], "properties.key")
              );
              if (feature) {
                featuresArr.push(feature);
              }
            }
          }
        }
      } else if (lodashGet(features[0], "properties.key")) {
        feature = getGeohashFeature(lodashGet(features[0], "properties.key"));
        if (feature) {
          featuresArr.push(feature);
        }
      }
      cluster_bbox = getBboxFromFeatures(featuresArr);
      const IS_CLUSTER = id.indexOf("cluster") > -1;
      mapClickEvent(e, cluster_bbox, IS_CLUSTER);
    }
  };
  layerClickEventHandlerOff(map, id);
  layerClickEventHandlerOn(map, id, MAP_CLICK_EVENT);
  map.off("mousemove", id, _showCursorPointerOnFeature);
  map.on("mousemove", id, _showCursorPointerOnFeature);
};

const _updateClusters = (e) => {
  for (let id in clusters) {
    if (clusters.hasOwnProperty(id)) {
      _updateCluster(id, e.target);
    }
  }
};

const _updateCluster = (id, map) => {
  if (!clusters.hasOwnProperty(id)) {
    clusters[id] = new supercluster(CLUSTER_OPTIONS);
  }

  const ZOOM = map.getZoom();
  const CLUSTER_DATA = featureCollection(
    clusters[id].getClusters([-180, -90, 180, 90], Math.floor(ZOOM))
  );

  const SOURCE = {
    type: "geojson",
    data: CLUSTER_DATA,
  };

  addSource(map, id + "-geohash", SOURCE);
};

const _addHeatmapSource = (id, map, data) => {
  addSource(map, id + "-geohash", {
    type: "geojson",
    data: data,
  });
};

const _getFeaturesInGeohashCluster = (sourceId, clusterId, limit, offset) => {
  if (clusters[sourceId]) {
    const SOURCE_CLUSTER = clusters[sourceId];
    limit = limit || Infinity;
    offset = offset || 0;
    return SOURCE_CLUSTER.getLeaves(clusterId, limit, offset);
  }
  return [];
};

const _showCursorPointerOnFeature = (e) => {
  e.target.getCanvas().style.cursor = "pointer";
};
